// Flow "Login depuis la marketplace".
//
// La marketplace envoie l'utilisateur sur  <dashboard>/login?redirect_to=<url courante>.
// Après authentification réussie :
//   - l'onglet courant retourne sur `redirect_to` (avec ?token=…, que la
//     marketplace lit puis retire de l'URL),
//   - le dashboard s'ouvre dans un NOUVEL onglet, déjà authentifié
//     (même origine => même localStorage).

const originOf = (value) => {
  try {
    return new URL(value).origin;
  } catch (e) {
    return null;
  }
};

// Anti open-redirect : le token n'est JAMAIS envoyé vers une origine autre que
// celle de REACT_APP_MARKET_PATH (ex: http://localhost:3000 en local,
// https://b2bmaroc.com en prod). Plusieurs origines possibles, séparées par une virgule.
// Variable absente/invalide => aucune redirection vers la marketplace.
const allowedOrigins = () =>
  (process.env.REACT_APP_MARKET_PATH || "")
    .split(",")
    .map((item) => originOf(item.trim()))
    .filter(Boolean);

// URL valide et autorisée (objet URL) ou null.
// Source unique : le paramètre ?redirect_to de l'URL courante. On ne lit PAS sessionStorage
// (temp_redirect_to) : une valeur périmée d'une visite précédente faisait partir le login
// vers la marketplace alors que l'utilisateur ouvrait simplement le dashboard.
export const getMarketplaceRedirect = () => {
  const raw = new URLSearchParams(window.location.search).get("redirect_to");
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return allowedOrigins().includes(url.origin) ? url : null;
  } catch (e) {
    return null;
  }
};

/**
 * À appeler quand token/user/isAuthenticated sont déjà dans localStorage.
 * `dashboardUrl` : URL à ouvrir dans un nouvel onglet. À ne passer que depuis
 * le handler de connexion (geste utilisateur récent), sinon popup bloquée.
 */
export const getMarketplaceOrigin = () => allowedOrigins()[0] || null;

/**
 * Logout global : la marketplace a son propre localStorage (autre origine), le dashboard
 * ne peut pas le vider lui-même. On passe donc par la marketplace :
 *   <marketplace>/?logout=1&redirect_to=<returnUrl>
 * La marketplace vide sa session puis renvoie vers `redirect_to` (qu'elle doit valider).
 * Retourne false si REACT_APP_MARKET_PATH est absent/invalide (aucune redirection).
 */
export const logoutFromMarketplace = (returnUrl) => {
  const origin = allowedOrigins()[0];
  if (!origin) return false;
  const url = new URL("/", origin);
  url.searchParams.set("logout", "1");
  if (returnUrl) url.searchParams.set("redirect_to", returnUrl);
  window.location.replace(url.toString());
  return true;
};

/**
 * Login direct sur le dashboard : on connecte aussi la marketplace en passant par elle :
 *   <marketplace>/?token=<token>&redirect_to=<returnUrl>
 * La marketplace enregistre le token puis renvoie vers `redirect_to` (qu'elle doit valider).
 * Retourne false si REACT_APP_MARKET_PATH est absent/invalide (aucune redirection).
 */
export const loginToMarketplace = (token, returnUrl) => {
  const origin = allowedOrigins()[0];
  if (!origin || !token) return false;
  const url = new URL("/", origin);
  url.searchParams.set("token", token);
  // la marketplace a besoin de `user` (company_id, nom...) et n'a pas d'endpoint pour le relire
  const user = localStorage.getItem("user");
  if (user) url.searchParams.set("user", user);
  if (returnUrl) url.searchParams.set("redirect_to", returnUrl);
  window.location.replace(url.toString());
  return true;
};

export const completeMarketplaceLogin = (target, token, dashboardUrl) => {
  if (dashboardUrl) {
    // Ouvrir via un <a target="_blank"> cliqué plutôt que window.open() : les navigateurs
    // gardent l'onglet courant actif dans ce cas, alors que window.open() bascule sur le
    // nouvel onglet (impossible à reprendre fiablement avec window.focus() ensuite).
    const link = document.createElement("a");
    link.href = dashboardUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const url = new URL(target.toString());
  url.searchParams.set("token", token);
  const user = localStorage.getItem("user");
  if (user) url.searchParams.set("user", user);
  window.location.replace(url.toString());
};
